import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import DashboardLayout from '../components/layouts/DashboardLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  MessageSquare, Send, ArrowLeft, Clock, CheckCircle, XCircle,
  Package, User, MapPin, DollarSign, Calendar, Loader2, Circle,
  Building2, Phone, Mail
} from 'lucide-react';
import LiveChat, { ChatButton } from '../components/shared/LiveChat';

const SellerInquiries = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { inquiryId } = useParams();
  const { user } = useAuth();
  const [inquiries, setInquiries] = useState([]);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [quotedPrice, setQuotedPrice] = useState('');
  const [sellerResponse, setSellerResponse] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [submittingQuote, setSubmittingQuote] = useState(false);
  const [showLiveChat, setShowLiveChat] = useState(false);

  const fetchInquiries = useCallback(async () => {
    try {
      const token = localStorage.getItem('atff_token');
      const response = await axios.get(`${API}/inquiries?role=seller`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInquiries(response.data.inquiries || []);
    } catch (error) {
      console.error('Error fetching inquiries:', error);
      toast.error('Failed to load inquiries');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchInquiryDetails = useCallback(async (id) => {
    try {
      const token = localStorage.getItem('atff_token');
      const response = await axios.get(`${API}/inquiries/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedInquiry(response.data.inquiry);
      setMessages(response.data.messages || []);
      if (response.data.inquiry.quoted_price) {
        setQuotedPrice(response.data.inquiry.quoted_price.toString());
      }
      if (response.data.inquiry.seller_response) {
        setSellerResponse(response.data.inquiry.seller_response);
      }
    } catch (error) {
      console.error('Error fetching inquiry details:', error);
      toast.error('Failed to load inquiry details');
    }
  }, []);

  useEffect(() => {
    fetchInquiries();
  }, [fetchInquiries]);

  useEffect(() => {
    if (inquiryId) {
      fetchInquiryDetails(inquiryId);
    }
  }, [inquiryId, fetchInquiryDetails]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedInquiry) return;

    setSendingMessage(true);
    try {
      const token = localStorage.getItem('atff_token');
      await axios.post(`${API}/inquiries/${selectedInquiry.inquiry_id}/message`, {
        inquiry_id: selectedInquiry.inquiry_id,
        content: newMessage
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setNewMessage('');
      fetchInquiryDetails(selectedInquiry.inquiry_id);
      toast.success('Message sent');
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleSubmitQuote = async () => {
    if (!quotedPrice || !sellerResponse) {
      toast.error('Please provide both a response message and quoted price');
      return;
    }

    setSubmittingQuote(true);
    try {
      const token = localStorage.getItem('atff_token');
      await axios.post(`${API}/inquiries/${selectedInquiry.inquiry_id}/respond`, {
        response: sellerResponse,
        quoted_price: parseFloat(quotedPrice)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Quote submitted successfully!');
      fetchInquiryDetails(selectedInquiry.inquiry_id);
      fetchInquiries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit quote');
    } finally {
      setSubmittingQuote(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: { bg: 'bg-yellow-100 text-yellow-700 border-yellow-200', icon: Clock },
      responded: { bg: 'bg-blue-100 text-blue-700 border-blue-200', icon: MessageSquare },
      negotiating: { bg: 'bg-purple-100 text-purple-700 border-purple-200', icon: MessageSquare },
      accepted: { bg: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle },
      rejected: { bg: 'bg-red-100 text-red-700 border-red-200', icon: XCircle },
      cancelled: { bg: 'bg-gray-100 text-gray-600 border-gray-200', icon: XCircle }
    };
    return styles[status] || styles.pending;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <Loader2 className="w-8 h-8 animate-spin text-continental" data-testid="seller-inquiries-loading" />
        </div>
      </DashboardLayout>
    );
  }

  // Detail View
  if (selectedInquiry) {
    const statusInfo = getStatusBadge(selectedInquiry.status);
    const StatusIcon = statusInfo.icon;
    const canSubmitQuote = selectedInquiry.status === 'pending' && !selectedInquiry.quoted_price;

    return (
      <DashboardLayout>
        <div className="max-w-5xl mx-auto" data-testid="seller-inquiry-detail">
          <Button
            variant="ghost"
            onClick={() => {
              setSelectedInquiry(null);
              setMessages([]);
              setQuotedPrice('');
              setSellerResponse('');
              navigate('/inquiries');
            }}
            className="mb-4"
            data-testid="back-to-inquiries-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Inquiries
          </Button>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Inquiry Details */}
            <Card className="lg:col-span-1">
              <CardHeader>
                <CardTitle className="text-lg">Inquiry Details</CardTitle>
                <Badge className={`w-fit ${statusInfo.bg}`}>
                  <StatusIcon className="w-3 h-3 mr-1" />
                  {selectedInquiry.status}
                </Badge>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm text-gray-500">Product</p>
                  <p className="font-medium">{selectedInquiry.product_name}</p>
                </div>
                
                {/* Buyer Info */}
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-500 mb-2">Buyer Information</p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-gray-400" />
                      <span className="font-medium">{selectedInquiry.buyer_name}</span>
                    </div>
                    {selectedInquiry.buyer_company && (
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-gray-400" />
                        <span className="text-sm">{selectedInquiry.buyer_company}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-gray-400" />
                      <span className="text-sm">{selectedInquiry.buyer_country || 'Not specified'}</span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Quantity</p>
                    <p className="font-medium">{selectedInquiry.quantity_needed} units</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Target Price</p>
                    <p className="font-medium">
                      {selectedInquiry.target_price ? `$${selectedInquiry.target_price}` : 'Not specified'}
                    </p>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Delivery To</p>
                  <p className="font-medium">{selectedInquiry.delivery_country}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Timeline</p>
                  <p className="font-medium">{selectedInquiry.delivery_timeline || 'Not specified'}</p>
                </div>
                
                {/* Quote Section */}
                {selectedInquiry.quoted_price ? (
                  <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                    <p className="text-sm text-green-600 font-medium">Your Quote</p>
                    <p className="text-xl font-bold text-green-700">${selectedInquiry.quoted_price} per unit</p>
                    <p className="text-sm text-green-600">
                      Total: ${(selectedInquiry.quoted_price * selectedInquiry.quantity_needed).toLocaleString()}
                    </p>
                  </div>
                ) : canSubmitQuote && (
                  <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <p className="text-sm text-amber-600 font-medium mb-2">Submit Your Quote</p>
                    <div className="space-y-3">
                      <div>
                        <label className="text-xs text-gray-500">Price per unit ($)</label>
                        <Input
                          type="number"
                          value={quotedPrice}
                          onChange={(e) => setQuotedPrice(e.target.value)}
                          placeholder="0.00"
                          className="mt-1"
                          data-testid="quote-price-input"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-gray-500">Response Message</label>
                        <Textarea
                          value={sellerResponse}
                          onChange={(e) => setSellerResponse(e.target.value)}
                          placeholder="Include terms, availability, shipping options..."
                          className="mt-1"
                          rows={3}
                          data-testid="quote-response-input"
                        />
                      </div>
                      <Button 
                        onClick={handleSubmitQuote}
                        disabled={submittingQuote || !quotedPrice || !sellerResponse}
                        className="w-full bg-continental hover:bg-continental/90"
                        data-testid="submit-quote-btn"
                      >
                        {submittingQuote ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        Submit Quote
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Messages */}
            <Card className="lg:col-span-2">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Conversation</CardTitle>
                <Button
                  onClick={() => setShowLiveChat(true)}
                  className="bg-green-600 hover:bg-green-700"
                  size="sm"
                  data-testid="open-live-chat-btn"
                >
                  <Circle className="w-3 h-3 mr-1 fill-green-300" />
                  Live Chat
                </Button>
              </CardHeader>
              <CardContent>
                <div className="h-[400px] overflow-y-auto space-y-4 mb-4 p-4 bg-gray-50 rounded-lg">
                  {/* Initial Inquiry from Buyer */}
                  <div className="flex justify-start">
                    <div className="max-w-[80%] bg-white border p-3 rounded-lg rounded-bl-sm shadow-sm">
                      <p className="text-xs font-medium text-gray-500 mb-1">{selectedInquiry.buyer_name}</p>
                      <p className="text-sm text-gray-700">{selectedInquiry.message}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {new Date(selectedInquiry.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>

                  {/* Seller Response */}
                  {selectedInquiry.seller_response && (
                    <div className="flex justify-end">
                      <div className="max-w-[80%] bg-continental text-white p-3 rounded-lg rounded-br-sm">
                        <p className="text-sm">{selectedInquiry.seller_response}</p>
                        {selectedInquiry.quoted_price && (
                          <p className="text-xs text-green-200 mt-1">
                            Quote: ${selectedInquiry.quoted_price}/unit
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Messages Thread */}
                  {messages.map((msg) => (
                    <div
                      key={msg.message_id}
                      className={`flex ${msg.sender_role === 'seller' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[80%] p-3 rounded-lg ${
                        msg.sender_role === 'seller'
                          ? 'bg-continental text-white rounded-br-sm'
                          : 'bg-white border shadow-sm rounded-bl-sm'
                      }`}>
                        {msg.sender_role !== 'seller' && (
                          <p className="text-xs font-medium text-gray-500 mb-1">{msg.sender_name}</p>
                        )}
                        <p className={`text-sm ${msg.sender_role === 'seller' ? 'text-white' : 'text-gray-700'}`}>
                          {msg.content}
                        </p>
                        <p className={`text-xs mt-1 ${msg.sender_role === 'seller' ? 'text-green-200' : 'text-gray-400'}`}>
                          {new Date(msg.created_at).toLocaleTimeString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Message Input */}
                <div className="flex gap-2">
                  <Input
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type your message..."
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                    disabled={selectedInquiry.status === 'accepted' || selectedInquiry.status === 'cancelled'}
                    data-testid="seller-message-input"
                  />
                  <Button
                    onClick={handleSendMessage}
                    disabled={sendingMessage || !newMessage.trim()}
                    className="bg-continental hover:bg-continental/90"
                    data-testid="seller-send-message-btn"
                  >
                    {sendingMessage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>

                {/* Live Chat Widget */}
                <LiveChat
                  inquiryId={selectedInquiry.inquiry_id}
                  currentUserId={user?.user_id}
                  otherPartyName={selectedInquiry.buyer_name}
                  otherPartyId={selectedInquiry.buyer_id}
                  productName={selectedInquiry.product_name}
                  isOpen={showLiveChat}
                  onClose={() => setShowLiveChat(false)}
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // List View
  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto" data-testid="seller-inquiries-list">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-charcoal">Buyer Inquiries</h1>
            <p className="text-gray-600">Respond to inquiries from potential buyers</p>
          </div>
          <div className="flex items-center gap-2">
            <Badge className="bg-yellow-100 text-yellow-700">
              {inquiries.filter(i => i.status === 'pending').length} pending
            </Badge>
          </div>
        </div>

        {inquiries.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-700 mb-2">No inquiries yet</h3>
              <p className="text-gray-500 mb-4">
                When buyers are interested in your products, their inquiries will appear here
              </p>
              <Button onClick={() => navigate('/my-products')} className="bg-continental hover:bg-continental/90">
                Manage Products
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {inquiries.map((inquiry) => {
              const statusInfo = getStatusBadge(inquiry.status);
              const StatusIcon = statusInfo.icon;

              return (
                <Card
                  key={inquiry.inquiry_id}
                  className="hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => {
                    setSelectedInquiry(inquiry);
                    navigate(`/inquiries/${inquiry.inquiry_id}`);
                  }}
                  data-testid={`inquiry-card-${inquiry.inquiry_id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-charcoal">{inquiry.product_name}</h3>
                          <Badge className={statusInfo.bg}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {inquiry.status}
                          </Badge>
                          {inquiry.status === 'pending' && (
                            <Badge className="bg-red-100 text-red-700 animate-pulse">New</Badge>
                          )}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div className="flex items-center gap-1 text-gray-600">
                            <User className="w-4 h-4" />
                            {inquiry.buyer_name}
                          </div>
                          <div className="flex items-center gap-1 text-gray-600">
                            <Package className="w-4 h-4" />
                            {inquiry.quantity_needed} units
                          </div>
                          <div className="flex items-center gap-1 text-gray-600">
                            <MapPin className="w-4 h-4" />
                            {inquiry.delivery_country}
                          </div>
                          <div className="flex items-center gap-1 text-gray-600">
                            <Calendar className="w-4 h-4" />
                            {new Date(inquiry.created_at).toLocaleDateString()}
                          </div>
                        </div>
                        {inquiry.target_price && (
                          <div className="mt-2 flex items-center gap-2">
                            <DollarSign className="w-4 h-4 text-amber-600" />
                            <span className="text-amber-600 font-medium">
                              Target: ${inquiry.target_price}/unit
                            </span>
                          </div>
                        )}
                        {inquiry.quoted_price && (
                          <div className="mt-1 flex items-center gap-2">
                            <DollarSign className="w-4 h-4 text-green-600" />
                            <span className="text-green-600 font-medium">
                              Your Quote: ${inquiry.quoted_price}/unit
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                        <ChatButton
                          inquiryId={inquiry.inquiry_id}
                          currentUserId={user?.user_id}
                          otherPartyName={inquiry.buyer_name}
                          otherPartyId={inquiry.buyer_id}
                          productName={inquiry.product_name}
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default SellerInquiries;
